import {
  Component,
  EventEmitter,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogComponent } from '@components/dialog/dialog.component';
import { ButtonComponent } from '@components/button/button.component';
import { RadioButtonComponent } from '@components/radio-button/radio-button.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LanguagesService } from '@common/services/languages.service';
import { filter, Subscription, take } from 'rxjs';

@Component({
  selector: 'app-change-language',
  standalone: true,
  imports: [
    TranslateModule,
    DialogComponent,
    ButtonComponent,
    RadioButtonComponent,
  ],
  templateUrl: './change-language.component.html',
  styleUrl: './change-language.component.scss',
})
export class ChangeLanguageComponent implements OnInit, OnChanges, OnDestroy {
  @Output() closeDialog = new EventEmitter<void>();

  openChangeLanguageDialog = input.required<boolean>();
  changeLanguageForm: FormGroup<{ language_key: any }> = this.fb.group({
    language_key: [null, Validators.required],
  });
  languagesList!: { key: string; label: any }[];
  isLoading = false;
  private langChangeSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private languagesService: LanguagesService
  ) {}

  ngOnInit(): void {
    this.initLanguageLabels();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['openChangeLanguageDialog'] &&
      changes['openChangeLanguageDialog']?.currentValue === true
    ) {
      const currentLang = this.languagesService.getCurrentLanguage();
      this.changeLanguageForm.patchValue({ language_key: currentLang });
    }
  }

  get isSubmitDisabled(): boolean {
    const selectedLangVal = this.changeLanguageForm.controls.language_key.value;
    const currentLang = this.languagesService.getCurrentLanguage();
    return (
      this.isLoading || !selectedLangVal || selectedLangVal === currentLang
    );
  }

  initLanguageLabels(): void {
    const loadLabels = () => {
      this.translate
        .get([
          'change_language_dialog_arabic_option_label',
          'change_language_dialog_english_option_label',
        ])
        .subscribe(translations => {
          this.languagesList = [
            {
              key: 'ar',
              label: translations['change_language_dialog_arabic_option_label'],
            },
            {
              key: 'en',
              label:
                translations['change_language_dialog_english_option_label'],
            },
          ];
        });
    };

    loadLabels();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      loadLabels();
    });
  }

  changeLanguage(): void {
    if (this.changeLanguageForm?.valid) {
      this.isLoading = true;
      const selectedLanguage = this.changeLanguageForm.value.language_key;
      this.languagesService.languageChanged$
        .pipe(
          filter(langChanged => langChanged === selectedLanguage),
          take(1)
        )
        .subscribe(() => {
          this.resetLanguageForm();
          this.isLoading = false;
        });
      this.languagesService.setCurrentLanguage(selectedLanguage);
    }
  }

  resetLanguageForm(): void {
    this.closeDialog.emit();
    this.changeLanguageForm.reset();
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }
}
